/**
 * Client-side hydrator for blockchain data
 * Initializes Reatom atoms with server-prefetched data
 *
 * Pattern:
 * 1. Server prefetches data and passes it as prop
 * 2. Hydrator initializes atoms on client using useMemo
 * 3. Components access initialized atoms
 */

"use client"

import type { BlockchainConfig } from "@anaconda/core/blockchain/types"
import { type PropsWithChildren, useMemo } from "react"
import { initializeBlockchainsData } from "./blockchain-molecule.reatom"

interface Props extends PropsWithChildren {
  blockchains?: { networks: readonly BlockchainConfig[] }
}

export function BlockchainsHydratorReatom({ blockchains, children }: Props) {
  // Use stringified networks as dependency to avoid infinite loops from object reference changes
  const networksKey = useMemo(
    () => (blockchains ? JSON.stringify(blockchains.networks) : null),
    [blockchains],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: networksKey is a stable stringified version of blockchains.networks to prevent infinite loops
  useMemo(() => {
    if (blockchains) {
      initializeBlockchainsData({
        data: blockchains,
        isLoading: false,
        isPending: false,
        error: undefined,
        status: "success",
        isError: false,
        isSuccess: true,
      })
    }
  }, [networksKey])

  return <>{children}</>
}
